import React from 'react';
import { LandingPageConfig } from 'src/generated/landing-page-config-schemas/v1/type';
import { Box, Container, Typography, useTheme } from '@mui/material';
import IconifyIcon from '../../components/IconifyIcon';

type Props = Extract<
  LandingPageConfig['data']['slices'][number],
  { type: 'value-proposition' }
> & {
  backgroundColor: string;
};

interface ValuePropCard {
  icon: { name: string };
  title: string;
  description: string;
}

const ValueProposition: React.FC<Props> = (props) => {
  const theme = useTheme();
  const { title, cards, backgroundColor } = props;

  return (
    <Box
      sx={{
        backgroundColor,
        py: 10,
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: '1312px', px: 2 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '32px', md: '48px' },
            fontWeight: 600,
            textAlign: { xs: 'left', md: 'center' },
            mb: 6,
            color: '#000000',
            px: { xs: 2, md: 0 },
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'stretch' },
            gap: 3,
          }}
        >
          {cards.map((card: ValuePropCard, index: number) => (
            <Box
              key={index}
              sx={{
                minHeight: { xs: '200px', md: '280px' },
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: '400px', md: '300px' },
                flex: { xs: 'none', md: 1 },
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E5E5',
                borderRadius: '16px',
                padding: { xs: '20px 16px', md: '24px 16px' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Icon container */}
              <Box
                sx={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.palette.primary.contrastText,
                }}
              >
                <Box
                  sx={{
                    fontSize: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconifyIcon name={card.icon.name} />
                </Box>
              </Box>

              <Box sx={{ height: { xs: '24px', md: '40px' } }} />
              <Box>
                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#000000',
                  }}
                >
                  {card.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '16px',
                    color: '#666666',
                    lineHeight: 1.5,
                    mt: 1,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {card.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ValueProposition;
